import { gql } from '@apollo/client';
import { Box, VStack } from '@chakra-ui/react';
import { DocumentNode } from 'graphql';
import NextLink from 'next/link';
import * as React from 'react';

import { AbbreviateRedraft } from '@/components/AbbreviateRedraft';
import { AttachmentDisplay } from '@/components/AttachmentDisplay';
import { useCommentActions } from '@/components/Post/hooks/useCommentActions';
import { usePostHref } from '@/hooks/usePostHref';
import {
  PlatformContextPreviewLink,
  useLinkPreview,
} from '@/imports/ui/components/context/PlatformContextPreviewLink';

import { Question_QuestionV2Fragment } from './graphql-types/Question_QuestionV2Fragment';

import { AnswerFooter } from '../AnswerFooter';
import { Comments } from '../Comments';
import { CreateComment, CreateComment_postFragment } from '../CreateComment';
import { ImageDisplay } from '../ImageDisplay';
import { LikeCommentFooter } from '../LikeCommentFooter';
import { PostHeader } from '../PostHeader';
import { Tags } from '../Tags';
import { Title } from '../Title';

type PostHeaderProps = React.ComponentProps<typeof PostHeader>;

export const Question: React.FC<{
  question: Question_QuestionV2Fragment;
  options?: PostHeaderProps['options'] & {
    hideSeeAllAnswers?: boolean;
    isExpandedByDefault?: boolean;
  };
}> & {
  questionFragment: DocumentNode;
} = ({ question, options }) => {
  const postHref = usePostHref(question._id);

  const { linkPreview } = useLinkPreview({
    rawDraftContentState: question.questionBodyContentState,
  });

  const {
    ref,
    focusAndScrollToRef,
    expandOrFocusAndScrollToRef,
    isActive,
    setIsActive,
    ...expandProps
  } = useCommentActions({
    post: question,
    isExpandedByDefault: options?.isExpandedByDefault,
  });

  return (
    <Box p="4">
      <PostHeader post={question} mb="4" options={options} />
      <NextLink href={postHref}>
        <Title>{question.questionTitle}</Title>
      </NextLink>
      {question.questionBodyContentState && (
        <AbbreviateRedraft
          options={{ defaultIsOpen: options?.isExpandedByDefault }}
          rawDraftContentState={question.questionBodyContentState}
          noOfCharacters={500}
        />
      )}
      <VStack spacing="4" width="full" align="start" mt="4">
        <PlatformContextPreviewLink
          linkPreview={linkPreview}
          canEdit={false}
          onClose={() => null}
          w="full"
        />
        <ImageDisplay postEmbeddedImage={question.embeddedImagesV2} />
        <AttachmentDisplay versionFiles={question.versionFiles} />
        <Tags post={question} />
        <AnswerFooter
          question={question}
          showSeeAllAnswers={
            !!options?.hideSeeAllAnswers ? false : question.answerCount > 0
          }
        />
        <LikeCommentFooter
          post={question}
          onClickComment={focusAndScrollToRef}
          onClickCommentCount={expandOrFocusAndScrollToRef}
        />
        <Comments post={question} expandProps={expandProps} />
        <CreateComment
          post={question}
          ref={ref}
          isActive={isActive}
          setIsActive={setIsActive}
        />
      </VStack>
    </Box>
  );
};

Question.questionFragment = gql`
  fragment Question_QuestionV2Fragment on QuestionV2 {
    _id
    questionTitle: title
    answerCount
    questionBodyContentState: bodyContentState {
      ...useLinkPreviewFragment
    }
    ...AnswerFooter_QuestionFragment
    ...PostHeader_PostFragment
    ...Tags_PostFragment
    ...LikeCommentFooter_PostFragment
    ...Comments_PostFragment
    ...CreateComment_PostFragment
    versionFiles {
      _id
      ...AttachmentDisplay_VersionFileFragment
    }
    embeddedImagesV2 {
      ...ImageDisplay_PostEmbeddedImageFragment
    }
    ...useCommentActions_PostFragment
  }
  ${useLinkPreview.rawDraftContentStateFragment}
  ${AnswerFooter.questionFragment}
  ${PostHeader.postFragment}
  ${Tags.postFragment}
  ${LikeCommentFooter.postFragment}
  ${Comments.postFragment}
  ${CreateComment_postFragment}
  ${AttachmentDisplay.versionFileFragment}
  ${ImageDisplay.postEmbeddedImageFragment}
  ${useCommentActions.postFragment}
`;
